/**
 * Mock notification and activity data for the SRE Dashboard.
 *
 * Aligned with SkyTech Corp demo scenario (MOCK_INCIDENTS).
 * Provides realistic demo data when the backend proxy is unavailable.
 */
import type {
  Notification,
  ActivityFeedItem,
  NotificationId,
  IncidentId,
  ExecutionId,
} from '../types';

// ── Helper: cast string to branded type ─────────────────────

function nid(value: string): NotificationId {
  return value as unknown as NotificationId;
}

function iid(value: string): IncidentId {
  return value as unknown as IncidentId;
}

function eid(value: string): ExecutionId {
  return value as unknown as ExecutionId;
}

// ── Notifications ───────────────────────────────────────────

export const MOCK_NOTIFICATIONS: readonly Notification[] = [
  {
    id: nid('notif-001'),
    category: 'alert',
    source: 'cloudwatch_alarm',
    severity: 'critical',
    title: 'CPUUtilization High - payment-service',
    description: 'CPU 使用率が 95% を超過。Grafana アラートにより自動検出。',
    status: 'unread',
    relatedIncidentId: iid('inc-001'),
    relatedExecutionId: null,
    linkHash: '#incidents',
    timestamp: '2026-02-23T09:00:00Z',
    actor: 'Grafana',
  },
  {
    id: nid('notif-002'),
    category: 'auto_response',
    source: 'runbook_execution',
    severity: 'critical',
    title: 'Runbook 自動実行: cpu-high-response',
    description: 'INC-2026-0001 に対してランブック cpu-high-response を自動実行開始。',
    status: 'unread',
    relatedIncidentId: iid('inc-001'),
    relatedExecutionId: eid('exec-001'),
    linkHash: '#runbooks',
    timestamp: '2026-02-23T09:00:10Z',
    actor: 'SuperAgent',
  },
  {
    id: nid('notif-003'),
    category: 'alert',
    source: 'cloudwatch_alarm',
    severity: 'high',
    title: 'ELB HealthCheck Failure - web-frontend',
    description: 'web-frontend の ELB ヘルスチェックが失敗。複数ターゲットが unhealthy 状態。',
    status: 'unread',
    relatedIncidentId: iid('inc-002'),
    relatedExecutionId: null,
    linkHash: '#incidents',
    timestamp: '2026-02-23T09:05:00Z',
    actor: 'CloudWatch',
  },
  {
    id: nid('notif-004'),
    category: 'auto_response',
    source: 'runbook_execution',
    severity: 'high',
    title: 'Runbook 自動実行: elb-recovery',
    description: 'INC-2026-0002 に対して elb-recovery ランブックを自動実行。承認待ちステップあり。',
    status: 'unread',
    relatedIncidentId: iid('inc-002'),
    relatedExecutionId: eid('exec-002'),
    linkHash: '#runbooks',
    timestamp: '2026-02-23T09:05:15Z',
    actor: 'SuperAgent',
  },
  {
    id: nid('notif-005'),
    category: 'alert',
    source: 'cost_anomaly',
    severity: 'medium',
    title: 'コスト異常検出: EC2 費用 +23%',
    description: '先月比で EC2 コストが 23% 増加。未使用インスタンスの可能性あり。',
    status: 'unread',
    relatedIncidentId: null,
    relatedExecutionId: null,
    linkHash: '#overview',
    timestamp: '2026-02-23T08:30:00Z',
    actor: 'AWS Cost Explorer',
  },
  {
    id: nid('notif-006'),
    category: 'activity',
    source: 'slo_breach',
    severity: 'high',
    title: 'SLO Breach: payment-api 可用性',
    description: 'payment-api の可用性 SLO（99.9%）がエラーバジェットの 80% を消費。',
    status: 'read',
    relatedIncidentId: null,
    relatedExecutionId: null,
    linkHash: '#slo',
    timestamp: '2026-02-23T07:45:00Z',
    actor: 'SLO Monitor',
  },
  {
    id: nid('notif-007'),
    category: 'auto_response',
    source: 'system',
    severity: 'low',
    title: '朝会チェックリスト完了',
    description: '2026-02-23 の朝会チェックリストが全項目完了。',
    status: 'read',
    relatedIncidentId: null,
    relatedExecutionId: null,
    linkHash: '#dailyOps',
    timestamp: '2026-02-23T08:00:00Z',
    actor: '山田太郎',
  },
  {
    id: nid('notif-008'),
    category: 'alert',
    source: 'databricks_job',
    severity: 'high',
    title: 'Databricks Job 失敗: daily-etl-pipeline',
    description: 'daily-etl-pipeline ジョブが OOM で失敗。自動リスタートを実行中。',
    status: 'unread',
    relatedIncidentId: iid('inc-004'),
    relatedExecutionId: null,
    linkHash: '#incidents',
    timestamp: '2026-02-23T08:15:00Z',
    actor: 'Databricks',
  },
  {
    id: nid('notif-009'),
    category: 'auto_response',
    source: 'runbook_execution',
    severity: 'high',
    title: 'Runbook 自動実行: databricks-job-restart',
    description: 'daily-etl-pipeline の OOM 対応: クラスタリサイズ + ジョブ再実行ランブックを自動実行。',
    status: 'unread',
    relatedIncidentId: iid('inc-004'),
    relatedExecutionId: eid('exec-004'),
    linkHash: '#runbooks',
    timestamp: '2026-02-23T08:15:30Z',
    actor: 'SuperAgent',
  },
  {
    id: nid('notif-010'),
    category: 'auto_response',
    source: 'runbook_execution',
    severity: 'low',
    title: 'Databricks ジョブ再起動完了: daily-etl-pipeline',
    description: 'クラスタを i3.2xlarge → i3.4xlarge にリサイズし、ジョブ再実行が正常完了。',
    status: 'read',
    relatedIncidentId: iid('inc-004'),
    relatedExecutionId: eid('exec-004'),
    linkHash: '#runbooks',
    timestamp: '2026-02-23T08:25:00Z',
    actor: 'SuperAgent',
  },
  {
    id: nid('notif-011'),
    category: 'activity',
    source: 'system',
    severity: 'low',
    title: '改善提案: キャッシュ導入で API レイテンシ改善',
    description: 'Performance Agent が Redis キャッシュ導入による p99 レイテンシ改善を提案。',
    status: 'read',
    relatedIncidentId: null,
    relatedExecutionId: null,
    linkHash: '#dailyOps',
    timestamp: '2026-02-22T16:00:00Z',
    actor: 'Performance Agent',
  },
];

// ── Activity Feed ───────────────────────────────────────────

export const MOCK_ACTIVITY_FEED: readonly ActivityFeedItem[] = [
  {
    id: 'act-001',
    type: 'incident_created',
    title: 'インシデント INC-2026-0001 作成',
    description: 'CPUUtilization High - payment-service',
    actor: 'Grafana',
    severity: 'critical',
    linkHash: '#incidents',
    timestamp: '2026-02-23T09:00:00Z',
  },
  {
    id: 'act-002',
    type: 'runbook_started',
    title: 'Runbook cpu-high-response 自動実行開始',
    description: 'INC-2026-0001 に対する自動対応',
    actor: 'SuperAgent',
    severity: 'critical',
    linkHash: '#runbooks',
    timestamp: '2026-02-23T09:00:10Z',
  },
  {
    id: 'act-003',
    type: 'incident_created',
    title: 'インシデント INC-2026-0002 作成',
    description: 'ELB HealthCheck Failure - web-frontend',
    actor: 'CloudWatch',
    severity: 'high',
    linkHash: '#incidents',
    timestamp: '2026-02-23T09:05:00Z',
  },
  {
    id: 'act-004',
    type: 'runbook_started',
    title: 'Runbook elb-recovery 自動実行開始',
    description: 'INC-2026-0002 に対する自動対応',
    actor: 'SuperAgent',
    severity: 'high',
    linkHash: '#runbooks',
    timestamp: '2026-02-23T09:05:15Z',
  },
  {
    id: 'act-005',
    type: 'approval_required',
    title: '承認待ち: ELB ターゲット切り替え',
    description: 'elb-recovery ランブックのステップ3が承認を待機中',
    actor: 'SuperAgent',
    severity: 'high',
    linkHash: '#runbooks',
    timestamp: '2026-02-23T09:06:00Z',
  },
  {
    id: 'act-006',
    type: 'incident_created',
    title: 'Databricks Job 失敗: daily-etl-pipeline',
    description: 'OOM エラーで daily-etl-pipeline が停止',
    actor: 'Databricks',
    severity: 'high',
    linkHash: '#incidents',
    timestamp: '2026-02-23T08:15:00Z',
  },
  {
    id: 'act-007',
    type: 'runbook_started',
    title: 'Runbook databricks-job-restart 自動実行開始',
    description: 'クラスタリサイズ + ジョブ再実行ランブック',
    actor: 'SuperAgent',
    severity: 'high',
    linkHash: '#runbooks',
    timestamp: '2026-02-23T08:15:30Z',
  },
  {
    id: 'act-008',
    type: 'databricks_job_restarted',
    title: 'Databricks ジョブ再起動完了: daily-etl-pipeline',
    description: 'i3.2xlarge → i3.4xlarge リサイズ後、ジョブ正常完了',
    actor: 'SuperAgent',
    severity: null,
    linkHash: '#runbooks',
    timestamp: '2026-02-23T08:25:00Z',
  },
  {
    id: 'act-009',
    type: 'cost_anomaly_detected',
    title: 'コスト異常検出: EC2 費用 +23%',
    description: '先月比で EC2 コストが 23% 増加',
    actor: 'AWS Cost Explorer',
    severity: 'medium',
    linkHash: '#overview',
    timestamp: '2026-02-23T08:30:00Z',
  },
  {
    id: 'act-010',
    type: 'slo_breach',
    title: 'SLO エラーバジェット警告: payment-api',
    description: 'エラーバジェットの 80% を消費',
    actor: 'SLO Monitor',
    severity: 'high',
    linkHash: '#slo',
    timestamp: '2026-02-23T07:45:00Z',
  },
  {
    id: 'act-011',
    type: 'runbook_completed',
    title: 'Runbook cpu-scale-out 完了',
    description: 'payment-service のスケールアウト処理が正常完了',
    actor: 'SuperAgent',
    severity: null,
    linkHash: '#runbooks',
    timestamp: '2026-02-23T09:10:00Z',
  },
  {
    id: 'act-012',
    type: 'incident_resolved',
    title: 'インシデント INC-2026-0001 解決',
    description: 'CPU 使用率が正常範囲に復帰',
    actor: '山田太郎',
    severity: null,
    linkHash: '#incidents',
    timestamp: '2026-02-23T09:15:00Z',
  },
  {
    id: 'act-013',
    type: 'kaizen_proposed',
    title: '改善提案: キャッシュ導入で API レイテンシ改善',
    description: 'Redis キャッシュ導入による p99 レイテンシ改善を提案',
    actor: 'Performance Agent',
    severity: null,
    linkHash: '#dailyOps',
    timestamp: '2026-02-22T16:00:00Z',
  },
];
